/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { Fragment, useMemo, useRef, useState } from 'react';
import Badge from 'components/v2/Badge';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { getFormatMMDDYYYY, isDateValid } from 'common/helpers/dateUtils';
import { cn } from 'utils/ui/classes';
import { Popover, Transition } from '@headlessui/react';
import CircleButton from 'components/v2/buttons/CircleButton';
import { useTranslation } from 'react-i18next';
import { filterOptionSlugs } from './columns';
import { titleize, humanize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';

const SHIPPER_FILTER_NAME = 'shipper_name';

export default function FilterChips({
  columnDefs,
  filterModel,
  className,
  onRemove,
  view
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const container = useRef(null);

  const showExpandButton = container.current?.scrollHeight > 20;

  const bodyClassNames = cn('flex flex-wrap gap-4 mb-2', {
    'h-5 overflow-hidden': !open,
  });

  const setFieldName = (acc, column) => {
    if (column.children) {
      column.children.reduce(setFieldName, acc);
    }

    acc[column.field] = column.headerName;
    return acc;
  };

  const filterNames = useMemo(
    () => columnDefs?.reduce(setFieldName, {}) || [],
    [view],
  );

  if (!filterModel || !Object.keys(filterModel).length) return null;

  const filters = Object.keys(filterModel)
    .filter((name) => Object.keys(filterModel[name]).length) // exclude empty filters
    .map((name) => ({
      name,
      ...filterModel[name],
    }));

  const renderFilter = (data) => {
    const { filterType } = data;

    if (data.name === SHIPPER_FILTER_NAME) {
      return renderCustomerFilter(data);
    }

    switch (filterType) {
      case 'date':
        return renderDateFilter(data);
      case 'number':
        return renderNumberFilter(data);
      case 'text':
        return renderTextFilter(data);
      case 'set':
        return renderSetFilter(data);
      default:
        return renderLsvFilter(data);
    }
  };

  const renderName = (name) => {
    return filterNames[name];
  };

  const renderType = (data, numeric = false) => {
    if (numeric) {
      if (data.type === 'equals') return '=';
      if (data.type === 'lessThan') return '<';
      if (data.type === 'lessThanOrEqual') return '<=';
      if (data.type === 'greaterThan') return '>';
      if (data.type === 'greaterThanOrEqual') return '>=';
    }

    return data.type === 'equals' ? ':' : t(filterOptionSlugs[data.type]);
  };

  const renderDates = (data) => {
    const dateFrom =
      (isDateValid(data.dateFrom) && getFormatMMDDYYYY(data.dateFrom)) || '';
    const dateTo =
      (isDateValid(data.dateTo) && getFormatMMDDYYYY(data.dateTo)) || '';
    const formattedDateRange = [];
    if (dateFrom) formattedDateRange.push(dateFrom);
    if (dateTo) formattedDateRange.push(dateTo);
    return formattedDateRange.join(" - ");
  };

  const renderOperator = (operator) => {
    return operator || 'AND';
  };

  const renderDateFilter = (data) => {
    const getCondition = (condition) => {
      return `${renderName(data.name)} ${renderType(condition)} ${renderDates(
        condition,
      )}`;
    };

    const conditions = data.conditions
      ? data.conditions.map((condition) => getCondition(condition))
      : [getCondition(data)];

    return conditions.join(` ${renderOperator(data.operator)} `);
  };

  const renderNumberFilter = (data) => {
    const getCondition = (condition) => {
      return `${renderType(condition, true)} ${condition.filter}`;
    };

    const conditions = data.conditions
      ? data.conditions.map((condition) => getCondition(condition))
      : [getCondition(data)];

    return `${renderName(data.name)} ${conditions.join(
      ` ${renderOperator(data.operator)} `,
    )}`;
  };

  const renderTextFilter = (data) => {
    const getCondition = (condition) => {
      return `${renderType(condition)}${
        condition.filter ? ' ' + condition.filter : ''
      }`;
    };

    const conditions = data.conditions
      ? data.conditions.map((condition) => getCondition(condition))
      : [getCondition(data)];

    return `${renderName(data.name)} ${conditions.join(
      ` ${renderOperator(data.operator)} `,
    )}`;
  };

  const renderCustomerFilter = (data) => {
    const getCondition = (condition) => {
      return `${renderType(condition)} ${condition.filter}`;
    };

    const values =
      data.filterModels[0]?.value?.map((value) => value.name) || [];

    const condition1 = data.filterModels[1]?.condition1
      ? getCondition(data.filterModels[1].condition1)
      : '';
    const condition2 = data.filterModels[1]?.condition2
      ? getCondition(data.filterModels[1].condition2)
      : '';

    const conditions = [condition1, condition2].filter(
      (condition) => condition,
    );

    const renderedValues = values ? `is ${values.join(` OR `)}` : '';
    const renderedConditions = `${conditions.join(
      ` ${renderOperator(data.filterModels[1]?.operator)} `,
    )}`;
    const middleOperator = renderedValues && renderedConditions ? 'OR' : '';

    return `${renderName(
      data.name,
    )} ${renderedValues} ${middleOperator} ${renderedConditions}`;
  };

  const renderLsvFilter = (data) => {
    const getCondition = (condition) => {
      return `${renderType(condition)} ${condition.filter}`;
    };

    const conditions = [];

    Object.keys(data).forEach((key) => {
      if (key.includes('condition')) {
        conditions.push(data[key]);
      }
    });

    if (conditions.length === 1) {
      return `${renderName(data.name)} ${getCondition(conditions[0])}`;
    }

    return (
      <>
        {renderName(data.name)} (
        {renderPopoverList(conditions.map((condition) => condition.filter))})
      </>
    );
  };

  const renderSetFilter = (data) => {
    const values = data.values.map((value) => titleize(humanize(t(sluggify(value)))));

    if (values.length === 1) {
      return `${renderName(data.name)}: ${values[0]}`;
    }

    return (
      <>
        {renderName(data.name)} ({renderPopoverList(values)})
      </>
    );
  };

  const renderPopoverList = (values) => {
    return (
      <Popover className="relative">
        <>
          <Popover.Button>
            <span className="text-blue-500 hover:text-blue-700">
              {values.length}
            </span>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="fixed z-10 mt-3 -translate-x-1/2 transform px-4 sm:px-0">
              <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black/5 w-64">
                <div className="relative bg-white px-4 py-2 divide-y divide-y-100 text-sm">
                  {values.map((value, i) => (
                    <p className="py-2" key={i}>
                      {value}
                    </p>
                  ))}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      </Popover>
    );
  };

  return (
    <div className={className}>
      <div className={bodyClassNames} ref={container}>
        {filters.map((filterData) => (
          <Badge
            withRemove
            color="blue"
            onRemove={() => {
              onRemove?.(filterData.name);
            }}
            key={filterData.name}
          >
            {renderFilter(filterData)}
          </Badge>
        ))}
      </div>

      {showExpandButton && (
        <CircleButton color="blue" size="small" onClick={() => setOpen(!open)}>
          {!open && <ChevronDownIcon className="w-5 h-5" />}
          {open && <ChevronUpIcon className="w-5 h-5" />}
        </CircleButton>
      )}
    </div>
  );
}
